import { useCallback } from 'react'
import { useAppLocale } from '@/components/i18n/locale-provider'
import type { Locale } from '@/lib/i18n/config'

/**
 * Byte sizes as the free audit shows them (812 B, 14.8 KB, 1.2 MB), with the
 * reader's decimal mark and unit names (fr "14,8 ko", de "14,8 kB").
 */
function formatAuditBytes(bytes: number, locale: Locale): string {
  if (bytes < 1024) {
    return `${new Intl.NumberFormat(locale).format(bytes)} B`
  }
  const isKilobytes = bytes < 1024 * 1024
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: isKilobytes ? 'kilobyte' : 'megabyte',
    unitDisplay: 'short',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(isKilobytes ? bytes / 1024 : bytes / 1024 / 1024)
}

/** `formatAuditBytes` in the reader's number format. */
export function useFormatAuditBytes(): (bytes: number) => string {
  const locale = useAppLocale()
  return useCallback(
    (bytes: number) => formatAuditBytes(bytes, locale),
    [locale]
  )
}
